import type { ReactNode } from 'react';
import { createContext, useContext, useRef } from 'react';
import { createStore, useStore } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { StateCreator } from '@shared-types/zustand.types';

export const zustandStoreFactory = <State extends object>(initializer: StateCreator<State>, storeName?: string) => {
  const createStoreInstance = (localState?: Partial<State>) => {
    const instance = createStore<State>()(devtools<State>(initializer, { name: storeName }));
    instance.setState(() => ({ ...localState }));
    return instance;
  };

  const CustomContext = createContext<ReturnType<typeof createStoreInstance>>(null);

  const StoreProvider = ({ children, state }: { children: ReactNode; state?: Partial<State> }) => {
    const store = useRef(createStoreInstance(state)).current;
    return <CustomContext value={store}>{children}</CustomContext>;
  };

  const useCustomStore = <T,>(selector: (state: State) => T): T => {
    const store = useContext(CustomContext);

    return useStore(store, selector);
  };

  return {
    Provider: StoreProvider,
    useStore: useCustomStore,
    createStore: createStoreInstance,
  };
};
